import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { logoutUser } from "../../redux/auth";
import { fetchProfile } from "../../redux/account";
import ChangeProfileModal from "../modals/ChangeProfileModal";
import PinEntryModal from "../modals/PinEntryModal";
import "../styles/navbar.scss";

/**
 * Navbar — MangoTV top navigation bar
 * Figma: Home Screen (Desktop) (24654:4806) — top nav section
 *
 * Layout: fixed top, full width, bg transparent → black on scroll
 *   - Left: LOGO
 *   - Center: TV | Movies | Live Shows | Sport
 *   - Right: Search icon | Profile avatar + dropdown
 *
 * Profile dropdown shows: Account, Settings, Logout
 * Real user data from Redux account.profile
 */
// Temporarily hidden per client: "TV Channels", "Live Show", "Podcast"
const NAV_ITEMS = ["Discover", /* "TV Channels", */ "Rent" /*, "Live Show", "Podcast" */];

const NAV_ROUTES = {
  Discover: "/discover",
  "TV Channels": "/tv-channels",
  Rent: "/rent",
  "Live Show": "/live",
  Podcast: "/podcast",
};

const Navbar = ({ activeTab = "Home" }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [changeProfileOpen, setChangeProfileOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [, setSelectedProfile] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);

  // Real user data from Redux
  const { profile, isProfileLoaded } = useSelector((state) => state.account);
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Dark background on scroll
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
        console.log('[Navbar] scroll state:', { scrollY: window.scrollY, hasBackground: isScrolled });
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  // Fetch profile if authenticated but not loaded
  useEffect(() => {
    if (isAuthenticated && !isProfileLoaded) {
      fetchProfile().catch(() => {});
    }
  }, [isAuthenticated, isProfileLoaded]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  // Derive display name and initial from profile
  const displayName =
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "User";

  const initial = displayName.charAt(0).toUpperCase();

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logoutUser();
  };

  return (
    <nav className={`mango-navbar ${scrolled ? "mango-navbar--scrolled" : ""}`}>
      <div className="mango-navbar__container">
        {/* Logo */}
        <div
          className="mango-navbar__logo"
          onClick={() => navigate("/home")}
          role="button"
          tabIndex={0}
        >
          <img
            src="/assets/logo/openplay-logo-color.png"
            alt="OpenPlay"
            style={{ height: "28px", width: "auto" }}
          />
        </div>

        {/* Nav Links (desktop) */}
        <ul className="mango-navbar__nav">
          {NAV_ITEMS.map((item) => (
            <li
              key={item}
              className={`mango-navbar__item ${
                activeTab === item ? "mango-navbar__item--active" : ""
              }`}
            >
              <button
                className="mango-navbar__link-btn"
                onClick={() => navigate(NAV_ROUTES[item])}
              >
                {item}
              </button>
            </li>
          ))}
        </ul>

        {/* Right actions */}
        <div className="mango-navbar__actions">
          {/* Search icon */}
          <button
            className="mango-navbar__icon-btn"
            aria-label="Search"
            onClick={() => navigate("/search")}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="#fafafa" strokeWidth="2" />
              <path
                d="M16.5 16.5L20 20"
                stroke="#fafafa"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {/* Watchlist / Bookmark icon
          <button
            className="mango-navbar__icon-btn"
            aria-label="Watchlist"
            onClick={() => navigate("/watchlist")}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"
                stroke="#fafafa"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button> */}

          {/* Profile button + dropdown */}
          <div className="mango-navbar__profile-wrapper" ref={dropdownRef}>
            <button
              className="mango-navbar__profile-btn"
              onClick={() => {
                if (window.innerWidth <= 768) {
                  setChangeProfileOpen(true);
                } else {
                  setDropdownOpen(!dropdownOpen);
                }
              }}
              aria-label="Profile menu"
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle
                  cx="16"
                  cy="16"
                  r="15"
                  fill="#1a1a6e"
                  stroke="#2a2a8e"
                  strokeWidth="1"
                />
                <text
                  x="16"
                  y="20"
                  textAnchor="middle"
                  fill="white"
                  fontSize="14"
                  fontWeight="600"
                >
                  {initial}
                </text>
              </svg>
              {/* <span className="mango-navbar__username">{firstName}</span> */}
              {/* Chevron */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className={`mango-navbar__chevron ${
                  dropdownOpen ? "mango-navbar__chevron--open" : ""
                }`}
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Dropdown */}
            {dropdownOpen && (
              <div className="mango-navbar__dropdown">
                {/* User info header */}
                <div className="mango-navbar__dropdown-header">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <circle
                      cx="20"
                      cy="20"
                      r="19"
                      fill="#1a1a6e"
                      stroke="#2a2a8e"
                      strokeWidth="1"
                    />
                    <text
                      x="20"
                      y="25"
                      textAnchor="middle"
                      fill="white"
                      fontSize="18"
                      fontWeight="600"
                    >
                      {initial}
                    </text>
                  </svg>
                  <div className="mango-navbar__dropdown-user">
                    <span className="mango-navbar__dropdown-name">
                      {displayName}
                    </span>
                    <span className="mango-navbar__dropdown-email">
                      {profile.email || ""}
                    </span>
                  </div>
                </div>

                <div className="mango-navbar__dropdown-divider" />

                {/* Menu items */}
                <button
                  className="mango-navbar__dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false);
                    setChangeProfileOpen(true);
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="6" cy="6" r="3" stroke="#a3a3a3" strokeWidth="1.5" />
                    <path d="M1 15c0-3 2-4.5 5-4.5s5 1.5 5 4.5" stroke="#a3a3a3" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M13 6v4M11 8h4" stroke="#a3a3a3" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Change Profile
                </button>

                <button
                  className="mango-navbar__dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/settings/account");
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="9" cy="5.5" r="3" stroke="#a3a3a3" strokeWidth="1.5" />
                    <path
                      d="M2.5 16c0-3.5 2.9-5 6.5-5s6.5 1.5 6.5 5"
                      stroke="#a3a3a3"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  Account
                </button>

                <button
                  className="mango-navbar__dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/watchlist");
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M14.25 15.75l-5.25-3.75-5.25 3.75V3.75a1.5 1.5 0 011.5-1.5h7.5a1.5 1.5 0 011.5 1.5z"
                      stroke="#a3a3a3"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  My Watchlist
                </button>

                <button
                  className="mango-navbar__dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/settings");
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M9 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z"
                      stroke="#a3a3a3"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M14.7 11.1l.6 1a.75.75 0 01-.27 1.03l-1.24.72a5.72 5.72 0 01-.46.8l.12 1.43a.75.75 0 01-.66.81l-1.44.17a5.8 5.8 0 01-.78.45l-1.1.92a.75.75 0 01-.97 0l-1.1-.92a5.8 5.8 0 01-.78-.45l-1.44-.17a.75.75 0 01-.66-.81l.12-1.44a5.72 5.72 0 01-.46-.79l-1.24-.72A.75.75 0 013.7 12.1l.6-1a5.82 5.82 0 010-.92l-.6-1a.75.75 0 01.27-1.03l1.24-.72c.13-.27.29-.54.46-.8l-.12-1.43a.75.75 0 01.66-.81l1.44-.17c.25-.17.51-.32.78-.45l1.1-.92a.75.75 0 01.97 0l1.1.92c.27.13.53.28.78.45l1.44.17a.75.75 0 01.66.81l-.12 1.44c.17.25.33.52.46.79l1.24.72a.75.75 0 01.27 1.03l-.6 1a5.82 5.82 0 010 .92z"
                      stroke="#a3a3a3"
                      strokeWidth="1.5"
                    />
                  </svg>
                  Settings
                </button>

                <button
                  className="mango-navbar__dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/settings/manage-device");
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <rect x="1.5" y="2" width="15" height="10" rx="1" stroke="#a3a3a3" strokeWidth="1.5" />
                    <path d="M5 12h8" stroke="#a3a3a3" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M7 15h4" stroke="#a3a3a3" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Manage Devices
                </button>

                <div className="mango-navbar__dropdown-divider" />

                <button
                  className="mango-navbar__dropdown-item mango-navbar__dropdown-item--logout"
                  onClick={handleLogout}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M6.75 15.75H3.75a1.5 1.5 0 01-1.5-1.5V3.75a1.5 1.5 0 011.5-1.5h3"
                      stroke="#ef4444"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 12.75L15.75 9 12 5.25M15.75 9H6.75"
                      stroke="#ef4444"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Log out
                </button>
              </div>
            )}
          </div>

          {/* Hamburger menu (mobile only) */}
          <button
            className="mango-navbar__hamburger"
            aria-label="Menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6H21M3 12H21M3 18H21"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile slide-down menu */}
      {mobileMenuOpen && (
        <div className="mango-navbar__mobile-menu">
          {NAV_ITEMS.map((item) => (
            <button
              key={item}
              className={`mango-navbar__mobile-item ${
                activeTab === item ? "mango-navbar__mobile-item--active" : ""
              }`}
              onClick={() => {
                navigate(NAV_ROUTES[item]);
                setMobileMenuOpen(false);
              }}
            >
              {item}
            </button>
          ))}
          <div className="mango-navbar__mobile-divider" />
          <button
            className="mango-navbar__mobile-item"
            onClick={() => {
              navigate("/watchlist");
              setMobileMenuOpen(false);
            }}
          >
            My Watchlist
          </button>
          <button
            className="mango-navbar__mobile-item"
            onClick={() => {
              navigate("/settings/account");
              setMobileMenuOpen(false);
            }}
          >
            Account
          </button>
          <button
            className="mango-navbar__mobile-item"
            onClick={() => {
              navigate("/settings");
              setMobileMenuOpen(false);
            }}
          >
            Settings
          </button>
          <button
            className="mango-navbar__mobile-item mango-navbar__mobile-item--logout"
            onClick={() => {
              setMobileMenuOpen(false);
              logoutUser();
            }}
          >
            Log out
          </button>
        </div>
      )}
      {/* Change Profile Modal */}
      <ChangeProfileModal
        isOpen={changeProfileOpen}
        onClose={() => setChangeProfileOpen(false)}
        onSelectProfile={(profile) => {
          setChangeProfileOpen(false);
          // TODO: switch active profile
        }}
        onPinRequired={(profile) => {
          setSelectedProfile(profile);
          setChangeProfileOpen(false);
          setPinModalOpen(true);
        }}
      />

      {/* PIN Entry Modal */}
      <PinEntryModal
        isOpen={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        onSubmit={(pin) => {
          setPinModalOpen(false);
          // TODO: verify PIN and switch profile
        }}
      />
    </nav>
  );
};

export default Navbar;
